import { AnyAction, Middleware } from 'redux';
import { DeepWritable } from 'ts-essentials';

import { notificationsApi } from 'apis/notifications';
import { AppState } from 'bootstrap.store';
import { notificationActions } from 'components/notifications/actions/notification';

const getStatusFromClassName = (className?: string) => {
  if (!className) {
    return 'default';
  }
  if (className.includes('success')) {
    return 'success';
  }
  if (className.includes('warning')) {
    return 'warning';
  }
  if (className.includes('danger')) {
    return 'danger';
  }
};

export const notificationMiddleware: () => Middleware<unknown, AppState> =
  () => (store) => {
    return (next) => (action: AnyAction) => {
      switch (action.type) {
        case notificationActions.notificationAdd.toString():
          store.dispatch(
            notificationsApi.util.updateQueryData(
              'readNotifications',
              { unreadOnly: false },
              (draft) => {
                if (!draft) return;

                const notification = action.payload;

                const mutableData = draft.data.notifications as DeepWritable<
                  typeof draft.data.notifications
                >;

                mutableData.push({
                  channel: '',
                  created_at: notification.timestamp ?? new Date().getTime(),
                  is_read: false,
                  notification_id: notification.id,
                  organization_id: '',
                  receiver_id: '',
                  sender_id: '',
                  payload: '',
                  payload_data: notification.payloadData ?? {
                    type: 'local_notification:displayed',
                    params: {
                      icon: notification.icon ?? 'i-info',
                      title: notification.title,
                      type: notification.type,
                      conversation: notification.conversation,
                      // In legacy NotificationT text could be a ReactNode. Storing it in RTQ store and then trying to render it results in error.
                      // Use type and treat these notification as a special case (similar to mention notification) where stylized text is necessary.
                      text:
                        typeof notification.text === 'object'
                          ? ''
                          : notification.text,
                      status:
                        notification.status ??
                        getStatusFromClassName(notification.className),
                    },
                  },
                  is_local: true,
                });
              },
            ),
          );
          return next(action);
        case notificationActions.notificationHistoryReset.toString():
          store.dispatch(
            notificationsApi.util.updateQueryData(
              'readNotifications',
              { unreadOnly: false },
              (draft) => {
                if (!draft) return;

                const mutableData = draft.data.notifications as DeepWritable<
                  typeof draft.data.notifications
                >;

                mutableData.forEach((notification) => {
                  if (notification.is_local && !notification.is_read) {
                    notification.is_read = true;
                  }
                });
              },
            ),
          );
          return next(action);
        case notificationActions.notificationHistoryRemove.toString():
        case notificationActions.notificationRemoveAllById.toString():
          const { id } = action.payload;
          store.dispatch(
            notificationsApi.util.updateQueryData(
              'readNotifications',
              { unreadOnly: false },
              (draft) => {
                if (!draft) return;

                const mutableData = draft.data as DeepWritable<
                  typeof draft.data
                >;

                mutableData.notifications = mutableData.notifications.filter(
                  ({ notification_id, is_local }) =>
                    is_local ? notification_id !== id : true,
                );
              },
            ),
          );
          return next(action);
        default:
          return next(action);
      }
    };
  };
